import React from 'react';
import { validatePasswordStrength, PasswordStrength } from '@/utils/passwordValidation';
import { Progress } from '@/components/ui/progress';
import { Check, X } from 'lucide-react';

interface PasswordStrengthIndicatorProps {
  password: string;
}

export const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({ password }) => {
  if (!password) return null;

  const strength = validatePasswordStrength(password);
  const progressValue = (strength.score / 5) * 100;

  const getProgressColor = () => {
    switch (strength.label) {
      case 'Weak': return 'bg-destructive';
      case 'Fair': return 'bg-orange-500';
      case 'Good': return 'bg-yellow-500';
      case 'Strong': return 'bg-green-500';
      default: return 'bg-muted';
    }
  };

  return (
    <div className="space-y-2 mt-2">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">Password Strength:</span>
        <span className={strength.color + ' font-medium'}>{strength.label}</span>
      </div>
      <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div 
          className={`h-full transition-all duration-300 ${getProgressColor()}`}
          style={{ width: `${progressValue}%` }}
        />
      </div>
      {strength.feedback.length > 0 && (
        <ul className="text-xs space-y-1 mt-2">
          {strength.feedback.map((item, index) => (
            <li key={index} className="flex items-center gap-1.5 text-muted-foreground">
              <X className="h-3 w-3 text-destructive flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const getPasswordStrength = validatePasswordStrength;
